import { diContainer, serviceNames } from '../diContainer/diContainer';
import ShippingRepository from '../repositories/ShippingRepository';
import BillingRepository from '../repositories/BillingRepository';
import NotificationsCollection from '../domain/entities/Notification/NotificationsCollection';
import SaveNewBillingAddressUseCase from './SaveNewBillingAddressUseCase';
import UpsSetValidationsUseCase from './ups/UpsSetValidationsUseCase';
import SaveNewShippingAddressUseCase from './SaveNewShippingAddressUseCase';
import AddressFormStore from '../domain/entities/AddressFormStore';
import Config, { IBackendValidation } from '../domain/entities/Config';
import EditShippingAddressUseCase from './EditShippingAddressUseCase';
import CompanyBillingDetailsRepository from '../repositories/CompanyBillingDetailsRepository';
import SaveCompanyBillingDetailsUseCase from './SaveCompanyBillingDetailsUseCase';
import { DeliveryOptions, FIELD_TYPE } from '../constants/Constants';
import Address from '../domain/entities/Address';
import normalizeApiErrors from '../helpers/normalizeApiErrors';
import { ADDRESS, RESTRICTED_ADDRESS_ERRORS } from '../constants/Errors';
import SelectBillingAddressUseCase from './SelectBillingAddressUseCase';
import TransactionLoadHandler, { ITransactionLoad } from './TransactionLoadHandler';
import { NOTIFICATION_TYPE } from '../constants/Notifications';

export const SHIPPING_FORM_ERROR = 'SHIPPING_FORM_ERROR';
export const SHIPPING_FORM_ERROR_SELECTED = 'SHIPPING_FORM_ERROR_SELECTED';
export const BILLING_FORM_ERROR = 'BILLING_FORM_ERROR';
export const BILLING_FORM_ERROR_SELECTED = ADDRESS.INVALID_BILLING_ADDRESS_SELECTED;
export const COMPANY_BILLING_FORM_ERROR = 'COMPANY_BILLING_FORM_ERROR';
export const PACKSTATION_FORM_ERROR = 'PACKSTATION_FORM_ERROR';
export const GIFTWRAP_ERROR = 'GIFTWRAP_ERROR';

export interface IAddressSubmissionConfig {
  isBillingSameAsShipping: boolean;
  withUpsValidation: boolean;
  backendValidation?: IBackendValidation;
  deliveryOption?: DeliveryOptions;
  shippingAddress?: Address;
}

const useCaseOptions: ITransactionLoad = {
  transactionLoadOptions: {
    isEnabled: false,
  },
};

class ValidateAndSubmitAddressesUseCase extends TransactionLoadHandler {
  private shippingRepository: ShippingRepository;

  private billingRepository: BillingRepository;

  private notificationsCollection: NotificationsCollection;

  private config: Config;

  private addressSubmissionConfig: IAddressSubmissionConfig;

  private companyBillingDetailsRepository: CompanyBillingDetailsRepository;

  constructor(addressSubmissionConfig: IAddressSubmissionConfig) {
    super({
      shouldAwait: true,
    });
    this.addressSubmissionConfig = {
      deliveryOption: DeliveryOptions.HOME_DELIVERY,
      isBillingSameAsShipping: true,
      withUpsValidation: false,
      ...(addressSubmissionConfig || {}),
    };
    const { deliveryOption } = this.addressSubmissionConfig;
    this.shippingRepository = deliveryOption === DeliveryOptions.PACKSTATION
      ? diContainer.get(serviceNames.packstationRepository)
      : diContainer.get(serviceNames.shippingRepository);
    this.billingRepository = diContainer.get(serviceNames.billingRepository);
    this.notificationsCollection = diContainer.get(serviceNames.notificationsCollection);
    this.config = diContainer.get(serviceNames.config);
    this.companyBillingDetailsRepository = diContainer.get(
      serviceNames.companyBillingDetailsRepository,
    );
  }

  public async run(): Promise<void> {
    this.notificationsCollection.clearNotifications(undefined, NOTIFICATION_TYPE.DANGER);
    const {
      transactionRepository,
      billingRepository,
      shippingRepository,
    } = this;
    const transaction = await transactionRepository.getTransaction();
    const {
      shippingAddress,
      user,
      selectedShippingMethodId,
      namedDelivery: {
        deliveryDate,
        deliveryTime,
      },
    } = transaction;

    const {
      deliveryOption,
      isBillingSameAsShipping,
      withUpsValidation,
    } = this.addressSubmissionConfig;

    // if is registered user and select packstation, it should be a new address
    // a guest user could already have an address created, hence it might not be a new address
    const isNewShippingOrGuestAddress = shippingAddress.isNewAddress
      || !user.isUserRegistered
      || (user.isUserRegistered && deliveryOption === DeliveryOptions.PACKSTATION);

    // validate forms
    await this.validateShippingAndBillingForms(isNewShippingOrGuestAddress);
    await this.validateCompanyDetailsForm();

    // validate address selections
    if (!isNewShippingOrGuestAddress) {
      await this.validateSelectionAddress();
    }

    const shippingAddressToUpdate = await this.getShippingAddressToUpdate();

    // handle ups validation that will trigger an error in case suggestions are available
    if (withUpsValidation) {
      await new UpsSetValidationsUseCase(shippingAddressToUpdate).execute();
    }

    // save new billing address
    if (!isBillingSameAsShipping) {
      const saveNewBillingAddressUseCase = new SaveNewBillingAddressUseCase(useCaseOptions);
      await saveNewBillingAddressUseCase.execute();
    }

    // save new company billing details
    if (this.config.canSetCompanyBilling) {
      await new SaveCompanyBillingDetailsUseCase(useCaseOptions).execute();
    }

    // save shipping address
    if (shippingAddressToUpdate.isNewAddress || deliveryOption === DeliveryOptions.PACKSTATION) {
      try {
        await new SaveNewShippingAddressUseCase(shippingAddressToUpdate, isBillingSameAsShipping, useCaseOptions).execute();
      } catch (error) {
        const notificationErrors = normalizeApiErrors(error);
        const hasRestrictedAddressNotifications = RESTRICTED_ADDRESS_ERRORS.some(key => notificationErrors.some(e => e.key === key));
        if (hasRestrictedAddressNotifications && user.isUserRegistered) {
          await this.transactionRepository.loadTransaction();
        }

        throw error;
      }
    } else if (!user.isUserRegistered || this.addressSubmissionConfig.shippingAddress) {
      // update shipping address for guest users or for updated address passed
      const updatedAddress = await shippingRepository.updateAddress(shippingAddressToUpdate);
      transaction.setShippingAddress(updatedAddress);
      if (isBillingSameAsShipping) {
        await billingRepository.selectAddress(updatedAddress.id);
        transaction.setBillingAddress(updatedAddress);
      }
    } else if (isBillingSameAsShipping) {
      await new SelectBillingAddressUseCase(shippingAddressToUpdate.id, useCaseOptions).execute();
    }

    // Usually shipping method doesn't have to be updated within shipping delivery submission use case
    // since that is being done while user selects it from the separate Shipping Methods Section.
    // But shipping method needs to be saved later (within below step) for the following scenarios:
    //
    // 1. When NAMED delivery option is available, as details on delivery date are sent only at this step
    if (deliveryDate || deliveryTime) {
      this.transactionRepository.setShippingMethod(selectedShippingMethodId, false, false);
    }
  }

  private async getShippingAddressToUpdate(): Promise<Address> {
    const transaction = await this.transactionRepository.getTransaction();
    const { deliveryOption, shippingAddress: suggestedAddress } = this.addressSubmissionConfig;
    const {
      shippingAddress,
      user,
    } = transaction;

    let address: Address = shippingAddress;

    if (suggestedAddress) {
      return suggestedAddress;
    }

    if (shippingAddress.isNewAddress || deliveryOption === DeliveryOptions.PACKSTATION) {
      // new address
      address = this.shippingRepository.formStore.toAddress();
    } else if (!user.isUserRegistered) {
      // guest address
      address = this.shippingRepository.formStore.toAddress();
      address.id = shippingAddress.id;
    }

    return address;
  }

  private async validateShippingAndBillingForms(
    isNewShippingOrGuestAddress: boolean,
  ): Promise<void> {
    const {
      isBillingSameAsShipping,
      deliveryOption,
    } = this.addressSubmissionConfig;

    const transaction = await this.transactionRepository.getTransaction();
    const {
      billingAddress,
      user,
    } = transaction;

    const shippingFormStore = this.shippingRepository.formStore;
    const billingFormStore = this.billingRepository.formStore;

    if ((isNewShippingOrGuestAddress && !shippingFormStore.isValid)
      || (billingAddress.isNewAddress && !billingFormStore.isValid)) {
      shippingFormStore.setShowErrors(!shippingFormStore.isValid);
      billingFormStore.setShowErrors(
        !user.isUserRegistered && isBillingSameAsShipping ? false : !billingFormStore.isValid,
      );
      if (!shippingFormStore.isValid) {
        if (deliveryOption === DeliveryOptions.PACKSTATION) {
          throw new Error(PACKSTATION_FORM_ERROR);
        } else {
          throw new Error(SHIPPING_FORM_ERROR);
        }
      } else if (!isBillingSameAsShipping) {
        throw new Error(BILLING_FORM_ERROR);
      }
    }
  }

  private async validateCompanyDetailsForm(): Promise<void> {
    if (!this.config.canSetCompanyBilling) return;

    const transaction = await this.transactionRepository.getTransaction();
    const {
      companyBillingDetails: {
        attributes: {
          [FIELD_TYPE.BILL_TO_COMPANY]: billToCompany,
        },
      },
    } = transaction;

    if (billToCompany && !this.companyBillingDetailsRepository.formStore.isValid) {
      this.companyBillingDetailsRepository.formStore.setShowErrors(
        !this.companyBillingDetailsRepository.formStore.isValid,
      );
      throw new Error(COMPANY_BILLING_FORM_ERROR);
    }
  }

  private async validateSelectionAddress(): Promise<void> {
    const transaction = await this.transactionRepository.getTransaction();
    const {
      shippingAddress,
      billingAddress,
    } = transaction;

    const {
      isBillingSameAsShipping,
    } = this.addressSubmissionConfig;

    const { jsonSchemasEnabled } = this.config;

    // if json schema enabled need to load json schema for selected shipping address validation
    if (jsonSchemasEnabled) {
      await this.shippingRepository.loadAddressSchema(shippingAddress.attributes[FIELD_TYPE.COUNTRY_ID]);
    }
    const validationSchema = this.shippingRepository.formStore.addressSchema;

    const isAddressValid = new AddressFormStore(this.config)
      .setAddress(shippingAddress)
      .validateAddressBySchema(validationSchema);

    if (!isAddressValid) {
      this.shippingRepository.formStore.setShowErrors(true);
      const useCase = new EditShippingAddressUseCase(
        shippingAddress.id, isBillingSameAsShipping,
      );
      await useCase.execute();
      throw new Error(SHIPPING_FORM_ERROR_SELECTED);
    }

    // handle backend validation for selected shipping address
    const backendValidation = this.config.getBackendValidateAddressConfig(shippingAddress.attributes[FIELD_TYPE.COUNTRY_ID]);
    if (backendValidation?.shouldValidateSavedShippingAddress) {
      try {
        await this.shippingRepository.validateAddress(shippingAddress, backendValidation.validationType);
      } catch (error) {
        const normalizedErrors = normalizeApiErrors(error);
        // highlight form fields for specified error type
        if (normalizedErrors.find(errorObj => errorObj.key === ADDRESS.INVALID_AUPOST_QAS_ADDRESS)) {
          this.shippingRepository.formStore.setShowErrors(true);
          new EditShippingAddressUseCase(
            shippingAddress.id, isBillingSameAsShipping, useCaseOptions,
          ).execute();
          throw new Error(ADDRESS.INVALID_AUPOST_QAS_ADDRESS);
        }
        throw error;
      }
    }

    if (!billingAddress.isNewAddress) {
      // if json schema enabled need to load json schema for selected billing address validation
      if (jsonSchemasEnabled) {
        await this.billingRepository.loadAddressSchema(billingAddress.attributes[FIELD_TYPE.COUNTRY_ID]);
      }
      const billingValidationSchema = this.billingRepository.formStore.addressSchema;

      const isBillingAddressValid = new AddressFormStore(this.config)
        .setAddress(billingAddress)
        .validateAddressBySchema(billingValidationSchema);

      if (!isBillingAddressValid) {
        throw new Error(BILLING_FORM_ERROR_SELECTED);
      }
    }
  }

  private shouldUseLegacySchema(): boolean {
    return !this.config?.jsonSchemasEnabled;
  }
}

export default ValidateAndSubmitAddressesUseCase;
